import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HubRecordEditorBase } from '../../_hub_record_editor_base';
import { HubLovService } from '../../../../core/services/hub-lov.service';
import { forkJoin, Subscription, take } from 'rxjs';
import { Goal2030Model, OutcomeMetricModel, ProjectGoalTargetModel, ProjectOutcomeMetricTargetModel } from '../../../../../hub_schema/hubTypes';
import { LocalStorageService } from '../../../../core/services/local-storage.service';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';

function metricTargetValidator(control: AbstractControl): ValidationErrors | null {
    const { targetDate, targetValue, targetStatement } = (control as FormGroup).controls;
    const hasAny = [targetDate, targetValue, targetStatement].some(c => c.value != null && c.value !== '');
    const corePaired = [targetDate, targetValue].every(c => c.value != null && c.value !== '');
    return hasAny && !corePaired ? { metricTargetIncomplete: 'Required' } : null;
}

function goalTargetValidator(control: AbstractControl): ValidationErrors | null {
    const { targetDate, targetValue, targetStatement } = (control as FormGroup).controls;
    const fields = [targetDate, targetValue, targetStatement];
    const hasAny = fields.some(c => c.value != null && c.value !== '');
    const hasAll = fields.every(c => c.value != null && c.value !== '');
    return hasAny && !hasAll ? { goalTargetIncomplete: 'Required' } : null;
}

@Component({
    selector: 'app-goal-targets-panel',
    templateUrl: './goal-targets-panel.component.html',
    styleUrl: './goal-targets-panel.component.scss',
    standalone: false
})
export class GoalTargetsPanelComponent extends HubRecordEditorBase implements OnInit, OnDestroy {
    private hubLovService: HubLovService = inject(HubLovService);
    private localStorageService: LocalStorageService = inject(LocalStorageService);
    public goalTargetForms: FormArray | undefined;
    private projectUpdatedSubscription: Subscription | undefined;
    private valueChangesSubscription: Subscription | undefined;

    private goals: Goal2030Model[] | undefined;
    private outcomeMetrics: OutcomeMetricModel[] | undefined;

    public ngOnInit(): void {
        this.projectUpdatedSubscription = this.hubRecordEditService.projectUpdated.subscribe(() => {
            if (this.valueChangesSubscription) {
                this.valueChangesSubscription?.unsubscribe();
            }
            this.createGoalForms();
        });

        forkJoin({
            goals: this.hubLovService.get2030Goals(),
            outcomeMetrics: this.hubLovService.getOutcomeMetrics()
        })
        .pipe(take(1)).subscribe((results: any) => {
            this.goals = results.goals;
            this.outcomeMetrics = results.outcomeMetrics;
            this.createGoalForms();
        });
    }

    private createGoalForms(): void {
        this.goalTargetForms = new FormArray<any>([]);

        this.goals!.forEach((goal: Goal2030Model) => {
            const existingProjectGoal = this.projectEditForm?.controls.projectGoalTargets.value
                .find((pg: ProjectGoalTargetModel) => pg.goalId === goal.goal2030Id);

            const goalTargetForm = new FormGroup<any>({
                id: new FormControl({
                    value: existingProjectGoal ? existingProjectGoal.id : undefined,
                    disabled: existingProjectGoal === undefined
                }),
                uniqueId: new FormControl({
                    value: existingProjectGoal ? existingProjectGoal.uniqueId : undefined,
                    disabled: true
                }),
                projectId: new FormControl(this.project.projectId),
                goalId: new FormControl(goal.goal2030Id),
                goal: new FormControl(goal),
                targetDate: new FormControl(existingProjectGoal ? existingProjectGoal.targetDate : undefined),
                targetValue: new FormControl(existingProjectGoal ? existingProjectGoal.targetValue : undefined),
                targetStatement: new FormControl(existingProjectGoal ? existingProjectGoal.targetStatement : undefined),
                metricTargets: new FormArray([])
            }, { validators: goalTargetValidator });

            const metrics = this.outcomeMetrics!.filter((om: OutcomeMetricModel) => om.goal2030Id === goal.goal2030Id);

            metrics.forEach((metric: OutcomeMetricModel) => {
                const existingMetricTarget = this.projectEditForm?.controls.projectOutcomeMetricTargets.value
                    .find((pomm: ProjectOutcomeMetricTargetModel) => pomm.outcomeMetricId === metric.outcomeMetricId);

                const metricTargetForm = new FormGroup<any>({
                    id: new FormControl({
                        value: existingMetricTarget ? existingMetricTarget.id : undefined,
                        disabled: existingMetricTarget === undefined
                    }),
                    uniqueId: new FormControl({
                        value: existingMetricTarget ? existingMetricTarget.uniqueId : undefined,
                        disabled: true
                    }),
                    projectId: new FormControl(this.project.projectId),
                    outcomeMetricId: new FormControl(metric.outcomeMetricId),
                    outcomeMetric: new FormControl(metric),
                    targetValue: new FormControl(existingMetricTarget ? existingMetricTarget.targetValue : undefined),
                    targetDate: new FormControl(existingMetricTarget ? existingMetricTarget.targetDate : undefined),
                    targetStatement: new FormControl(existingMetricTarget ? existingMetricTarget.targetStatement : undefined)
                }, { validators: metricTargetValidator });

                (goalTargetForm.controls.metricTargets as FormArray).push(metricTargetForm);

            });

            this.goalTargetForms!.push(goalTargetForm);
        });

        this.valueChangesSubscription = this.goalTargetForms.valueChanges.subscribe(() => {
            this.syncGoalTargetsToForm();
        });

    }

    public isGoalExpanded(goalId: number): boolean {
        const localStorageKey = `project_${this.project.projectId}_goal_${goalId}_expanded`;
        const localStorageValue = LocalStorageService.getItem(localStorageKey);

        if (localStorageValue != null) {
            return localStorageValue === "true";
        }
        else {
            LocalStorageService.setItem(localStorageKey, "false");
            return false;
        }
    }

    public goalExpanded(goalId: number): void {
        const localStorageKey = `project_${this.project.projectId}_goal_${goalId}_expanded`;
        LocalStorageService.setItem(localStorageKey, "true");
    }

    public goalCollapsed(goalId: number): void {
        const localStorageKey = `project_${this.project.projectId}_goal_${goalId}_expanded`;
        LocalStorageService.setItem(localStorageKey, "false");
    }

    public getLocalValidationErrorMessages(form: FormGroup, formControlName: string): string {
        const formControl = form.controls[formControlName];

        const targetFields = ['targetDate', 'targetValue', 'targetStatement'];
        if (form.errors?.['goalTargetIncomplete'] && targetFields.includes(formControlName) && !formControl.value) {
            return form.errors['goalTargetIncomplete'];
        }

        const metricTargetFields = ['targetDate', 'targetValue'];
        if (form.errors?.['metricTargetIncomplete'] && metricTargetFields.includes(formControlName) && !formControl.value) {
            return form.errors['metricTargetIncomplete'];
        }

        if (formControl.errors) {
            const errorMessages: string[] = [];

            for (const key of Object.keys(formControl.errors)) {
                if (key == 'required') {
                    errorMessages.push(key);
                }
                else {
                    errorMessages.push(formControl.errors[key]);
                }
            }
            return errorMessages.join(', ');
        }

        return '';
    }

    private syncGoalTargetsToForm(): void {
        const updatedGoals: ProjectGoalTargetModel[] = [];
        const updatedMetrics: ProjectOutcomeMetricTargetModel[] = [];

        for (const goalForm of (this.goalTargetForms!.controls as FormGroup[])) {
            const { id, projectId, goalId, targetDate, targetValue, targetStatement, metricTargets } = goalForm.controls;

            const isTouched = [targetDate, targetValue, targetStatement].some(c => c.value != null && c.value !== '');
            if (isTouched) {
                updatedGoals.push({ id: id.value, projectId: projectId.value, goalId: goalId.value, targetDate: targetDate.value, targetValue: targetValue.value, targetStatement: targetStatement.value });
            }

            for (const metricForm of ((metricTargets as FormArray).controls as FormGroup[])) {
                const { id: mId, projectId: mProjectId, outcomeMetricId, targetDate: mTargetDate, targetValue: mTargetValue, targetStatement: mTargetStatement } = metricForm.controls;

                const isMetricTouched = [mTargetDate, mTargetValue].some(c => c.value != null && c.value !== '');
                if (isMetricTouched) {
                    updatedMetrics.push({ id: mId.value, projectId: mProjectId.value, outcomeMetricId: outcomeMetricId.value, targetDate: mTargetDate.value, targetValue: mTargetValue.value, targetStatement: mTargetStatement.value });
                }
            }
        }

        const goalsControl = this.projectEditForm!.controls.projectGoalTargets;
        const metricsControl = this.projectEditForm!.controls.projectOutcomeMetricTargets;

        goalsControl.setValue(updatedGoals);
        goalsControl.markAsDirty();
        metricsControl.setValue(updatedMetrics);
        metricsControl.markAsDirty();

        const errors = this.goalTargetForms!.valid ? null : { invalidGoalTargets: true };
        goalsControl.setErrors(errors);
        metricsControl.setErrors(errors);
    }

    public ngOnDestroy(): void {
        if (this.projectUpdatedSubscription) {
            this.projectUpdatedSubscription?.unsubscribe();
        }
        if (this.valueChangesSubscription) {
            this.valueChangesSubscription?.unsubscribe();
        }
    }
}
